import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { updateUserProfile } from '../../services/userService';

interface EditProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { currentUser, userProfile, updateUserProfile: updateContext } = useAuth();

    const [formData, setFormData] = useState({
        name: '',
        companyName: '',
        phoneNumber: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Initialize form data when modal opens
    useEffect(() => {
        if (isOpen && userProfile) {
            setFormData({
                name: userProfile.name || '',
                companyName: userProfile.companyName || '',
                phoneNumber: userProfile.phoneNumber || ''
            });
            setError('');
        }
    }, [isOpen, userProfile]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Check if there are any changes made to the form
    const hasChanges = () => {
        if (!userProfile) return false;

        return (
            formData.name.trim() !== (userProfile.name || '') ||
            formData.companyName !== (userProfile.companyName || '') ||
            formData.phoneNumber !== (userProfile.phoneNumber || '')
        );
    };

    // Check if name field is valid (not empty after trimming)
    const isNameValid = () => {
        return formData.name.trim().length > 0;
    };

    const handleSave = async () => {
        if (!currentUser?.uid || !hasChanges() || !isNameValid()) return;

        try {
            setLoading(true);
            setError('');

            // Prepare data to send (only include changed fields)
            const updates: { name?: string; companyName?: string; phoneNumber?: string } = {};

            if (formData.name.trim() !== (userProfile?.name || '')) {
                updates.name = formData.name.trim();
            }
            if (formData.companyName !== (userProfile?.companyName || '')) {
                updates.companyName = formData.companyName;
            }
            if (formData.phoneNumber !== (userProfile?.phoneNumber || '')) {
                updates.phoneNumber = formData.phoneNumber;
            }

            // This shouldn't happen now due to validation, but keeping as safeguard
            if (Object.keys(updates).length === 0) {
                onClose();
                return;
            }

            const response = await updateUserProfile(currentUser.uid, updates);

            if (response.success) {
                // Update the context with new data
                updateContext(updates);
                onSuccess();
                onClose();
            } else {
                setError('Failed to update profile. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update profile:', err);
            setError('Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        // Reset form data to original values
        if (userProfile) {
            setFormData({
                name: userProfile.name || '',
                companyName: userProfile.companyName || '',
                phoneNumber: userProfile.phoneNumber || ''
            });
        }
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 relative">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-text-heading">Edit Profile</h2>
                    <button
                        onClick={handleCancel}
                        className="text-text-description hover:text-text-description"
                    >
                        <i className="fas fa-times text-lg"></i>
                    </button>
                </div>

                {/* Error message */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {error}
                    </div>
                )}

                {/* Form */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-text-heading mb-1">
                            Full Name
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-accent-taupe rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                            placeholder="Enter your full name"
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-heading mb-1">
                            Company Name
                        </label>
                        <input
                            type="text"
                            value={formData.companyName}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            className="w-full px-3 py-2 border border-accent-taupe rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                            placeholder="Enter your company name (optional)"
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-heading mb-1">
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            value={formData.phoneNumber}
                            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                            className="w-full px-3 py-2 border border-accent-taupe rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                            placeholder="Enter your phone number (optional)"
                            disabled={loading}
                        />
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 mt-6">
                    <button
                        onClick={handleCancel}
                        disabled={loading}
                        className="flex-1 bg-accent-taupe text-text-heading py-2 px-4 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading || !hasChanges() || !isNameValid()}
                        className="flex-1 bg-secondary-500 text-white py-2 px-4 rounded-md hover:bg-secondary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Saving...
                            </div>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </div>

                {/* Info text */}
                <p className="text-xs text-text-description text-center mt-4">
                    All changes will be saved to your profile immediately.
                </p>
            </div>
        </div>
    );
};

export default EditProfileModal;
